import { createContext, useContext } from 'react';
import type { LiveLlmCall, ProgressTree } from '@localagent/sdk';

export const LiveLlmCallsContext = createContext<ReadonlyMap<string, LiveLlmCall>>(new Map());

export function useLiveLlmCall(callId: string | undefined): LiveLlmCall | undefined {
  const byId = useContext(LiveLlmCallsContext);
  return callId ? byId.get(callId) : undefined;
}

export function streamingAnswerFor(
  run: ProgressTree | null | undefined,
  calls: LiveLlmCall[],
): LiveLlmCall | undefined {
  if (!run || run.status !== 'in_progress') return undefined;
  const rootIds = new Set(run.nodes.filter((n) => !n.parentId).map((n) => n.id));
  return calls
    .filter((c) => rootIds.has(c.taskId) && c.content !== '' && c.toolArgs === '')
    .at(-1);
}

export function llmStatusPhrase(call: LiveLlmCall): string {
  switch (call.state) {
    case 'waiting':
      return 'waiting on model';
    case 'receiving':
      return 'receiving';
    case 'retrying':
      return `retrying (attempt ${call.attempt})`;
    case 'stalled':
      return 'stalled · no output';
    case 'done':
      return 'done';
    case 'failed':
      return call.failure === 'budget_exceeded'
        ? 'took too long and was stopped'
        : call.failure === 'cancelled'
          ? 'stopped'
          : 'failed';
  }
}

const NESTED_RUN_NAMES: Readonly<Record<string, string>> = { image_read: 'Image reader' };

export function nestedRunName(toolName: string): string | undefined {
  return NESTED_RUN_NAMES[toolName];
}

export function latestNestedCall(calls: Iterable<LiveLlmCall>, childId: string): LiveLlmCall | undefined {
  let latest: LiveLlmCall | undefined;
  for (const call of calls) {
    if (call.nested && call.taskId === childId && (!latest || call.receivedAt > latest.receivedAt)) {
      latest = call;
    }
  }
  return latest;
}

export function useNestedLiveCall(childId: string | undefined): LiveLlmCall | undefined {
  const byId = useContext(LiveLlmCallsContext);
  return childId ? latestNestedCall(byId.values(), childId) : undefined;
}
